/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  DateInput,
  Option,
  Loader,
  Switch,
  Typography,
  UtilitySelect,
  useTheme,
  Icon,
  Tooltip,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useCallback, useEffect, useState } from 'react';
import { COUNTRY_OPTIONS } from 'utils/countries';
import { ExportCsv } from '../../../components/ExportCsv';
import {
  useTriggeredRulesApi,
  TriggeredRulesFilters,
  RulesOverview,
} from '../api';
import { RuleOverviewTable, RuleViolationTable } from './components';

export const TriggeredRules = () => {
  const { spacing, palette } = useTheme();
  const { useGetTriggeredRules, getCSV, useDryRunRule, useToggleRule } =
    useTriggeredRulesApi();
  const toggleRuleMutation = useToggleRule();

  const [selectedRule, setSelectedRule] = useState<RulesOverview | undefined>();

  const [filterState, setFilterState] = useState<TriggeredRulesFilters>({
    dateFilterRange: [null, null],
    selectedRuleName: null,
  });

  useEffect(() => {
    setFilterState((state) => ({
      ...state,
      selectedRuleName: selectedRule?.name || null,
    }));
  }, [selectedRule]);

  const { data, isFetching } = useGetTriggeredRules(filterState);

  // Sync selectedRule with refreshed data (e.g., after toggle)
  useEffect(() => {
    if (selectedRule && data.length > 0) {
      const updatedRule = data.find((r) => r.name === selectedRule.name);
      if (updatedRule && updatedRule.isEnabled !== selectedRule.isEnabled) {
        setSelectedRule(updatedRule);
      }
    }
  }, [data, selectedRule]);

  const {
    data: dryRunResult = '',
    isLoading: isDryRunning,
    refetch: performDryRun,
  } = useDryRunRule(selectedRule ? selectedRule.name : '');

  const exportFunction = useCallback(async () => {
    return await getCSV(filterState);
  }, [filterState, getCSV]);

  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr repeat(1, auto)',
            alignItems: 'center',
            gap: spacing.xxs,
          }}
        >
          {!selectedRule && (
            <Typography variant="subtitle" color={palette.grey.x300}>
              Rules engine
            </Typography>
          )}
          {selectedRule && (
            <div
              css={{ display: 'flex', gap: spacing.xxxs, alignItems: 'center' }}
            >
              <div
                css={{
                  cursor: 'pointer',
                  ':hover': {
                    textDecoration: 'underline',
                    textDecorationColor: palette.grey.x300,
                  },
                }}
                onClick={() => setSelectedRule(undefined)}
              >
                <Typography variant="subtitle" color={palette.grey.x300}>
                  Rules engine
                </Typography>
              </div>
              <div
                css={{
                  transform: 'rotate(180deg)',
                  svg: {
                    fill: 'none',
                    stroke: palette.grey.x300,
                  },
                }}
              >
                <Icon name="chevron" />
              </div>
              <Typography variant="subtitle">{selectedRule.name}</Typography>
            </div>
          )}
          <ExportCsv csvFileName="Rules" exportFunction={exportFunction} />
        </div>
      </Header>
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xxxs,
          flexWrap: 'wrap',
          marginBottom: spacing.xs,
        }}
      >
        <DateInput
          placeholder="All time"
          type="range"
          value={filterState.dateFilterRange}
          onChange={(e: [Date | null, Date | null]) => {
            setFilterState((state) => ({
              ...state,
              dateFilterRange: e,
            }));
          }}
          withTime
        />
        <UtilitySelect
          label={
            filterState.loginCountry
              ? COUNTRY_OPTIONS.find(
                  (countryOption) =>
                    countryOption.value === filterState.loginCountry
                )?.label
              : 'Country'
          }
          listTitle="Country"
          options={COUNTRY_OPTIONS}
          value={
            filterState.loginCountry
              ? COUNTRY_OPTIONS.find(
                  (countryOption) =>
                    countryOption.value === filterState.loginCountry
                ) || null
              : null
          }
          onSelect={(data) => {
            setFilterState((state) => ({
              ...state,
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              loginCountry: (data as Option)?.value,
            }));
          }}
        />
      </div>
      {!selectedRule && (
        <RuleOverviewTable
          overviews={data}
          handleRowClicked={(row) => {
            setSelectedRule(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              data.find((overview) => overview.name === row.ruleName)
            );
          }}
        />
      )}
      {selectedRule && (
        <>
          {selectedRule.description && (
            <div
              css={{
                marginBottom: spacing.xs,
                maxWidth: '60ch',
                whiteSpace: 'pre-wrap',
              }}
            >
              <Typography variant="body">{selectedRule.description}</Typography>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.xxs,
              marginBottom: spacing.s,
            }}
          >
            <Typography variant="body">Rule enabled</Typography>
            <Switch
              checked={selectedRule.isEnabled !== false}
              disabled={toggleRuleMutation.isPending}
              onChange={() =>
                toggleRuleMutation.mutate({
                  ruleName: selectedRule.name,
                  isEnabled: selectedRule.isEnabled === false,
                })
              }
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              borderColor: palette.grey.x300,
              borderRadius: '8px',
              borderWidth: '1px',
              borderStyle: 'solid',
              justifyContent: 'flex-start',
              marginBottom: '2em',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                margin: '1em',
              }}
            >
              <Tooltip title="Performs a run of the selected rule against sample data without making any changes and reports back what the outcome would be.">
                <UtilityButton
                  onClick={() => performDryRun()}
                  disabled={isDryRunning}
                  text={isDryRunning ? 'Running...' : 'Dry Run'}
                />
              </Tooltip>
            </div>
            {dryRunResult && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  flexDirection: 'column',
                  margin: '1em',
                }}
              >
                <Typography variant="subtitle" color={palette.grey.x300}>
                  Output
                </Typography>
                <div style={{ marginTop: '0.5em' }}>
                  <Typography variant="body">{dryRunResult}</Typography>
                </div>
              </div>
            )}
          </div>
          <RuleViolationTable violations={selectedRule.violations} />
        </>
      )}
      {isFetching && <Loader />}
    </>
  );
};
